import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Paper,
  MenuItem,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import Navbar from "./AdminNav";
import api from "../api/api";
import { useNavigate } from "react-router-dom";

const AddNewUserPage = () => {
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);

const [form, setForm] = useState({
  firstName: "",
  lastName: "",
  email: "",
  userName: "",
  accountType: "",
});

const [errors, setErrors] = useState({
  firstName: "",
  lastName: "",
  email: "",
  userName: "",
  accountType: "",
});

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") =>
    setToast({ open: true, message, severity });
  const closeToast = () => setToast({ ...toast, open: false });

  /* ---------------------- VALIDATION FUNCTIONS ---------------------- */
  const validateEmail = (email) => {
    if (!email) return false;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return regex.test(email);
  };

  const validateName = (name) => {
    if (!name) return false;
    return /^[A-Za-z\s]+$/.test(name);
  };

  const validateUsername = (username) => {
    if (!username) return false;
    return /^[A-Za-z0-9_.-]{3,30}$/.test(username);
  };

  /* ---------------------- LOAD ROLES & STATES ---------------------- */
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get("/api/Admin/GetAllRoles");
        setRoles(res.data?.data || []);
      } catch (err) {
        console.error("Error loading roles:", err);
      }
    };
    fetchRoles();
  }, []);

  /* ---------------------- HANDLE CHANGE ---------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    let errMsg = "";

    if (name === "firstName" && !validateName(value))
      errMsg = "First name should contain only letters.";
    if (name === "lastName" && !validateName(value))
      errMsg = "Last name should contain only letters.";
    if (name === "email" && value && !validateEmail(value))
      errMsg = "Invalid email format.";
    if (name === "userName" && value && !validateUsername(value))
      errMsg = "Username must be 3–30 chars, no spaces.";

    if (name === "accountType" && !value)
      errMsg = "Select a role.";

    setErrors((prev) => ({ ...prev, [name]: errMsg }));
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------------------- SUBMIT ---------------------- */
  const handleSubmit = async () => {
    // REQUIRED validation
    if (
  !form.firstName ||
  !form.lastName ||
  !form.email ||
  !form.userName ||
  !form.accountType
) {
      return showToast("Please fill all required fields.", "error");
    }

    // Any running validation error?
    const hasErrors = Object.values(errors).some((e) => e !== "");
    if (hasErrors) {
      return showToast("Please fix the errors before submitting.", "error");
    }

    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      userName: form.userName,
      accountType: form.accountType,
      state: "",
      city: "",
      password: "",
    };

    try {
      const res = await api.post("/api/User/AddNewUserDetails", payload, {
        headers: { "Content-Type": "application/json" },
      });

      const success =
        res.data?.status?.toLowerCase() === "ok" ||
        res.data?.httpStatusCode === 200;

      if (success) {
        showToast("User added successfully!", "success");
        setTimeout(() => navigate("/admin/user-list"), 1200);
      } else {
        showToast(res.data?.message || "Failed to add user!", "error");
      }
    } catch (err) {
      const apiMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message;

      showToast(apiMessage || "Something went wrong", "error");
    }
  };

  return (
    <>
      <Navbar />

      <Box sx={{ maxWidth: "600px", margin: "auto", padding: 3 }}>
        <Typography variant="h4" mb={3}>
          Add New User
        </Typography>

        <Paper sx={{ padding: 3 }}>
          {/* FIRST NAME */}
          <TextField
            label="First Name *"
            name="firstName"
            fullWidth
            margin="normal"
            value={form.firstName}
            onChange={handleChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
          />

          {/* LAST NAME */}
          <TextField
            label="Last Name *"
            name="lastName"
            fullWidth
            margin="normal"
            value={form.lastName}
            onChange={handleChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
          />

          {/* EMAIL */}
          <TextField
            label="Email *"
            name="email"
            type="email"
            fullWidth
            margin="normal"
            value={form.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />

          {/* USERNAME */}
          <TextField
            label="Login Name / Username *"
            name="userName"
            fullWidth
            margin="normal"
            value={form.userName}
            onChange={handleChange}
            error={!!errors.userName}
            helperText={errors.userName}
          />

          {/* ROLE */}
          <TextField
            label="Account Type *"
            name="accountType"
            select
            fullWidth
            margin="normal"
            value={form.accountType}
            onChange={handleChange}
            error={!!errors.accountType}
            helperText={errors.accountType}
          >
            {roles.map((r) => (
              <MenuItem key={r.roleId} value={r.roleId}>
                {r.roleName}
              </MenuItem>
            ))}
          </TextField>

          {/* ACTION BUTTONS */}
          <Box mt={3} textAlign="right">
            <Button
              variant="outlined"
              sx={{ mr: 2 }}
              onClick={() => navigate("/admin/user-list")}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={Object.values(errors).some((e) => e !== "")}
            >
              Add User
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* TOAST */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={closeToast}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddNewUserPage;
